import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Music, Play, Pause, Trash2, RefreshCw, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "./mk";
import { uploadMedia, mediaUrl, fmtSec } from "./mkMedia";

export const MusicPicker = ({ reel, music, onMusic, onChange }) => {
  const fileRef = useRef(null);
  const audioRef = useRef(null);
  const [pct, setPct] = useState(null);
  const [playing, setPlaying] = useState(false);
  const dur = music?.duration || 0;
  const start = reel.music_start || 0;
  const end = reel.music_end || dur;

  useEffect(() => { if (audioRef.current) audioRef.current.volume = Math.min(1, reel.music_volume ?? 1); }, [reel.music_volume]);

  const pick = async (file) => {
    if (!file) return;
    setPct(0);
    try {
      const m = await uploadMedia(file, "audio", setPct);
      onMusic(m);
      onChange({ music_id: m.id, music_start: 0, music_end: Math.min(m.duration, 60), music_volume: reel.music_volume ?? 1 });
      toast.success("Music added");
    } catch (e) { toast.error(errMsg(e, "Music upload failed")); }
    finally { setPct(null); }
  };
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); return; }
    a.currentTime = start;
    a.play().then(() => setPlaying(true)).catch(() => toast.error("Tap again to play"));
  };
  const onTime = () => { const a = audioRef.current; if (a && a.currentTime >= end) { a.pause(); setPlaying(false); } };
  const remove = () => { audioRef.current?.pause(); setPlaying(false); onMusic(null); onChange({ music_id: "", music_start: 0, music_end: 0 }); };

  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-3" data-testid="music-picker">
      <div className="flex items-center gap-2"><Music className="h-4 w-4 text-pink-600" /><span className="font-semibold text-sm">Music</span>
        <span className="text-[11px] text-slate-500">MP3, M4A or WAV · up to 20 MB · use music you have rights to</span></div>
      <input ref={fileRef} type="file" accept=".mp3,.m4a,.wav,audio/mpeg,audio/mp4,audio/wav,audio/x-m4a" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid="music-file-input" />
      {pct !== null ? (
        <div className="flex items-center gap-2 text-sm text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Uploading {pct}%</div>
      ) : !music ? (
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="music-upload-btn"><Upload className="mr-1 h-4 w-4" /> Upload music</Button>
      ) : (
        <div className="space-y-3">
          <audio ref={audioRef} src={mediaUrl(music.path)} preload="auto" onTimeUpdate={onTime} onEnded={() => setPlaying(false)} />
          <div className="flex items-center gap-2 flex-wrap">
            <Button type="button" size="sm" onClick={toggle} className="bg-pink-600 hover:bg-pink-700" data-testid="music-play-btn">{playing ? <><Pause className="mr-1 h-4 w-4" /> Pause</> : <><Play className="mr-1 h-4 w-4" /> Play</>}</Button>
            <span className="text-xs text-slate-600 truncate max-w-[180px]" data-testid="music-name">{music.filename}</span>
            <Button type="button" size="sm" variant="ghost" onClick={() => fileRef.current?.click()} data-testid="music-replace-btn"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Replace</Button>
            <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={remove} data-testid="music-remove-btn"><Trash2 className="mr-1 h-3.5 w-3.5" /> Remove</Button>
          </div>
          <div>
            <div className="flex justify-between text-xs text-slate-600 mb-1"><span>Use from {fmtSec(start)} to {fmtSec(end)}</span><span>{fmtSec(end - start)} of {fmtSec(dur)}</span></div>
            <Slider min={0} max={dur} step={0.5} value={[start, end]} minStepsBetweenThumbs={2}
              onValueChange={([s, e]) => onChange({ music_start: s, music_end: e })} data-testid="music-trim-slider" />
          </div>
          <div>
            <div className="text-xs text-slate-600 mb-1">Music volume: {Math.round((reel.music_volume ?? 1) * 100)}%</div>
            <Slider min={0} max={1} step={0.05} value={[Math.min(1, reel.music_volume ?? 1)]} onValueChange={([v]) => onChange({ music_volume: v })} data-testid="music-volume-slider" />
          </div>
        </div>
      )}
    </div>
  );
};
